import type { ReactNode } from "react";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";

import { useQuery } from "@tanstack/react-query";

import { useLibrary } from "../../../providers/useLibrary.hook";
import { useFeature } from "../features";
import { centsToDollars } from "../../../utils";

import { useFetchSupportedCurrencies } from "./requests";

import type { components } from "./schema";

export type InternationalWire = components["schemas"]["InternationalWire"];

export function createInternationalWireFromAPISwiftTransaction(
  apiSwiftTransaction: API.SwiftTransaction,
): InternationalWire {
  const {
    id,
    amount,
    from_account,
    destination,
    recipient,
    created_at,
    updated_at,
    wire_reason,
    memo,
    note,
    state,
    quote,
    template_name,
  } = apiSwiftTransaction;
  return {
    id,
    amount: centsToDollars(amount),
    fromAccountId: from_account,
    swiftDestinationId: destination,
    createdAt: created_at,
    updatedAt: updated_at,
    wireReason: wire_reason,
    recipientId: recipient,
    memo,
    note,
    state,
    quote,
    templateName: template_name,
  };
}

type InternationalWiresContext = {
  isLoading: boolean;
  data: InternationalWire[];
  error?: Error;
  refresh: () => void;
};

export const internationalWiresContext =
  createContext<InternationalWiresContext>({
    isLoading: false,
    data: [],
    error: undefined,
    refresh: () => {},
  });

export const useInternationalWiresContext = () =>
  useContext(internationalWiresContext);

export const InternationalWiresProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [lastUpdateRequested, setLastUpdateRequested] = React.useState<Date>(
    new Date(),
  );
  const { fetchApi, token } = useLibrary("network");
  const { featureEnabled } = useFeature();
  const queryFn = () => {
    if (!token || !featureEnabled("international_wires")) {
      return Promise.resolve([] as InternationalWire[]);
    }
    return fetchApi("swift_transactions")
      .then((response) => response.json())
      .then((responseData) => responseData.results as API.SwiftTransaction[])
      .then((apiSwiftTransactions) =>
        apiSwiftTransactions.map(
          createInternationalWireFromAPISwiftTransaction,
        ),
      );
  };
  const { isLoading, data, error } = useQuery({
    queryKey: [
      "swift_transactions",
      lastUpdateRequested.getTime(),
      featureEnabled("international_wires"),
      fetchApi,
      token,
    ],
    queryFn,
  });
  const refresh = useCallback(() => setLastUpdateRequested(new Date()), []);
  const value = useMemo(
    () => ({
      isLoading,
      data: data || [],
      error: error || undefined,
      refresh,
    }),
    [isLoading, data, error, refresh],
  );
  return (
    <internationalWiresContext.Provider value={value}>
      {children}
    </internationalWiresContext.Provider>
  );
};

type CurrencyContextType = {
  currencies: Array<API.CurrencyItem> | null;
  getCurrencies: () => Promise<Array<API.CurrencyItem> | undefined>;
  selectedCurrency: API.CurrencyItem | null;
  setSelectedCurrency: (currency: API.CurrencyItem | null) => void;
};
const currencyContext = createContext<CurrencyContextType>({
  currencies: null,
  getCurrencies: () => Promise.resolve([] as Array<API.CurrencyItem>),
  selectedCurrency: null,
  setSelectedCurrency: () => {},
});
export const useCurrencyContext = () => useContext(currencyContext);

export const CurrencyProvider = (props: { children: React.ReactNode }) => {
  const [currencies, setCurrencies] = useState<Array<API.CurrencyItem> | null>(
    null,
  );
  const [selectedCurrency, setSelectedCurrency] =
    useState<API.CurrencyItem | null>(null);
  const { fetchSupportedCurrencies } = useFetchSupportedCurrencies();
  const setNoCurrencies = () => {
    // we fetched currencies and there were none
    setCurrencies([]);
    return [];
  };
  const getCurrencies: () => Promise<Array<API.CurrencyItem> | undefined> =
    useCallback(() => {
      if (currencies) return Promise.resolve(currencies);
      return fetchSupportedCurrencies()
        .then((response) => {
          if (!response) {
            return setNoCurrencies();
          }
          setCurrencies(response);
          return response;
        })
        .catch(setNoCurrencies);
    }, [currencies, fetchSupportedCurrencies]);

  return (
    <currencyContext.Provider
      value={{
        currencies,
        getCurrencies,
        selectedCurrency,
        setSelectedCurrency,
      }}
    >
      {props.children}
    </currencyContext.Provider>
  );
};
